import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const LOCK_IMG = "https://static.prod-images.emergentagent.com/jobs/1b0b3fd3-e19a-43a9-907c-68e9e32f9aea/images/78706560e48b0d42748011f8c166ea53a5ff61ef330dda4ed7377d672a888529.png";

/**
 * LockConfirmModal — Phase 20.3 (mobile-first rebuild)
 * --------------------------------------------------------------
 * BEFORE: rigid `grid grid-cols-2` on every viewport. On mobile this
 * squeezed both the image and the text panel to ~half the screen width
 * and pushed the [ LOCK IT IN ] button off the bottom of the viewport,
 * making it impossible to start the league from a phone.
 *
 * AFTER: stacked `flex-col` on mobile (image on top, text + CTA below),
 * `md:flex-row` from ≥768px. The DialogContent is height-capped at
 * `max-h-[92dvh]` and its scrollable body uses `overflow-y-auto` so the
 * action row is ALWAYS reachable — even on the smallest phones.
 *
 * The primary CTA is now a full-width terminal-style [ ENGAGE RULES ]
 * button anchored at the bottom of the panel; Cancel sits above as a
 * less-prominent secondary action so the engage button is the obvious
 * tap target.
 */
export default function LockConfirmModal({ open, league, userCount, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent
        className="w-[95vw] max-w-2xl max-h-[92dvh] bg-[var(--stakes-surface)] border-[var(--stakes-primary)] border-2 rounded-none p-0 [&>button]:hidden flex flex-col overflow-hidden"
        data-testid="lock-confirm-modal"
      >
        <DialogTitle className="sr-only">Confirm league start</DialogTitle>
        <DialogDescription className="sr-only">Lock habit rules for the month</DialogDescription>

        {/* Scrollable body — stacks vertical on mobile, side-by-side on md+ */}
        <div className="flex-1 overflow-y-auto flex flex-col md:flex-row min-h-0">
          {/* Hero image — full width on mobile (capped height), left col on desktop */}
          <div className="bg-black flex items-center justify-center p-6 md:p-8 md:w-1/2 md:flex-shrink-0">
            <img
              src={LOCK_IMG}
              alt="Lock"
              className="max-h-32 md:max-h-72 object-contain"
            />
          </div>

          {/* Body */}
          <div className="p-6 md:p-8 md:w-1/2 flex flex-col">
            <div className="label-mono" style={{ color: "var(--stakes-primary)" }}>FINAL WARNING</div>
            <h2 className="font-anton text-3xl md:text-4xl uppercase leading-none mt-2">
              Lock The<br/>Rules.
            </h2>
            <p className="font-barlow text-base md:text-lg mt-5 md:mt-6 text-[var(--stakes-muted)]">
              Starting <span className="text-white font-bold">{league?.name || "the league"}</span> will
              freeze every player&apos;s 5 habit rules for the entire month. No edits. No swaps. No mercy.
            </p>
            <div className="mt-4 border border-[var(--stakes-border)] p-4">
              <div className="label-mono">PLAYERS LOCKED IN</div>
              <div className="font-anton text-3xl">{userCount}</div>
              <div className="label-mono mt-3">PRIZE POT</div>
              <div className="font-barlow text-xl font-bold uppercase break-words">
                {league?.monthly_prize || "—"}
              </div>
            </div>
          </div>
        </div>

        {/* Action row — pinned at the bottom of the modal so it's always tappable
            regardless of body height. safe-bottom keeps it clear of iOS home indicator. */}
        <div
          className="flex-shrink-0 border-t border-[var(--stakes-border)] bg-black/80 backdrop-blur px-4 py-4 md:px-8 md:py-5 safe-bottom"
          data-testid="lock-confirm-action-row"
        >
          <button
            onClick={confirm}
            disabled={busy}
            className="w-full bg-[var(--neon-blue)] text-black font-anton uppercase tracking-wider text-xl py-4 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-[filter]"
            data-testid="lock-confirm-button"
          >
            {busy ? "[ ENGAGING… ]" : "[ ENGAGE RULES ]"}
          </button>
          <button
            onClick={onClose}
            disabled={busy}
            className="mt-3 w-full h-11 border border-[var(--stakes-border)] label-mono hover:bg-white hover:text-black hover-flip disabled:opacity-50"
            data-testid="lock-cancel-button"
          >
            CANCEL
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
